<template>
  <!--
    Laid out like the rows of the About card next to it: one badge, one column of text,
    one control on the right. The bootstrap list-group this replaced brought padding of
    its own, which is what left the card's header, rows and footer on three different
    left edges.
  -->
  <div class="tw:flex tw:flex-col tw:gap-5">
    <div
      v-for="type in preferences"
      :key="type.value"
      class="tw:flex tw:items-start tw:gap-3"
    >
      <div class="tw:flex tw:items-center tw:justify-center tw:w-9 tw:h-9 tw:rounded-lg tw:bg-primary/10 tw:text-primary tw:shrink-0">
        <Icon :name="type.icon" />
      </div>

      <div class="tw:flex tw:flex-col tw:min-w-0 tw:grow">
        <label :for="`subscription-${type.value}`" class="tw:text-sm tw:font-medium tw:m-0">
          {{ type.label }}
        </label>

        <!--
          The project's .btn-link only underlines on hover, which left this reading as a
          caption. It is underlined at rest instead, and keeps a colour of its own: grey
          towards the general preference, the link blue towards a setting this event
          holds by itself.
        -->
        <button
          type="button"
          data-testid="scope"
          class="tw:inline-flex tw:items-center tw:gap-1.5 tw:self-start tw:p-0 tw:border-0 tw:bg-transparent tw:text-[11px] tw:underline tw:underline-offset-2 tw:cursor-pointer tw:whitespace-nowrap"
          :class="isInherited(type)
            ? 'tw:text-zinc-500 tw:hover:text-zinc-800'
            : 'tw:text-primary tw:hover:opacity-80'"
          :disabled="busy[type.value]"
          @click="switchScope(type)"
        >
          <Icon :name="isInherited(type) ? 'Pen' : 'Globe'" class="tw:shrink-0" />
          <span>
            {{ isInherited(type) ? t('Set a preference for this event') : t('Follow my general preference') }}
          </span>
        </button>
      </div>

      <!--
        While the event follows the general preference there is nothing of its own to
        switch, so the toggle is disabled rather than hidden: where it sits is what the
        event is getting, and that is worth showing.
      -->
      <Toggle
        :id="`subscription-${type.value}`"
        :model-value="type.effective"
        :disabled="busy[type.value] || isInherited(type)"
        class="tw:shrink-0 tw:mt-1"
        @update:modelValue="decide(type, $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import axios from 'axios'
import Toggle from '@/components/ui/Toggle.vue'
import { Icon } from '@/components/ui/icon'
import { toast } from 'vue-sonner'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * config/ziggy.php exposes only a handful of named routes to the browser, so these are
 * built from the base url the rest of the backoffice api layer already uses.
 */
const api = (path) => `${window.Laravel.backofficeApi}/${path}`

const props = defineProps({
  subscribableType: {
    type: String,
    required: true,
  },
  subscribableId: {
    required: true,
  },
})

const preferences = ref([])
const busy = ref({})

/** Nothing was said about this event, so it follows whatever holds elsewhere. */
const isInherited = (type) => type.scoped === null || type.scoped === undefined

const load = async () => {
  try {
    const { data } = await axios.get(api('notification-preference'), {
      params: {
        subscribable_type: props.subscribableType,
        subscribable_id: props.subscribableId,
      },
    })
    preferences.value = data.data
  } catch (error) {
    console.error('Failed to load notification preferences:', error)
    toast.error(t('Failed to update notification subscription'))
  }
}

const run = async (type, apply, request) => {
  const previous = { ...type }
  busy.value[type.value] = true
  apply()

  try {
    await request()
    toast.success(t('Notification preference saved'))
  } catch (error) {
    Object.assign(type, previous)
    console.error('Failed to update subscription:', error)
    toast.error(t('Failed to update notification subscription'))
  } finally {
    busy.value[type.value] = false
  }
}

const decide = (type, subscribed) => run(
  type,
  () => Object.assign(type, { scoped: subscribed, effective: subscribed }),
  () => axios.post(api('notification-subscription'), {
    type: type.value,
    subscribed,
    subscribable_type: props.subscribableType,
    subscribable_id: props.subscribableId,
  }),
)

/**
 * Giving the event a setting of its own starts it where it already was, so the switch
 * never moves on its own; going back to the general preference clears it.
 */
const switchScope = (type) => isInherited(type)
  ? decide(type, type.effective)
  : run(
    type,
    () => Object.assign(type, {
      scoped: null,
      effective: type.global ?? type.subscribedByDefault,
    }),
    () => axios.delete(api('notification-preference'), {
      data: {
        type: type.value,
        subscribable_type: props.subscribableType,
        subscribable_id: props.subscribableId,
      },
    }),
  )

onMounted(load)
</script>
